import '@css/Video.css'

import ImageIcon from '@assets/images/image.svg'
import VideoIcon from '@assets/images/video.svg'
import { tvModeActive } from '@classes/app_url_params'
import GlassButton from '@components/GlassButton'
import Loader from '@components/Loader'
import ThreeDView from '@components/ThreeD/ThreeDView'
import MainWrapper from '@components/Utils/MainWrapper'
import type { AppState } from '@constants/types'
import { useDemoMedia } from '@hooks/useDemoMedia'
import { useYisNavigate } from '@hooks/useYisNavigate'
import { Box, GlobalStyles, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import { motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

export enum TYPE {
  PHOTO = 'shipImage',
  VIDEO = 'shipOwner'
}

// `childrenContainerClassName` is passed to MainWrapper as the `containerClass`
// string prop, which lands on an internal Grid we don't render here, so its CSS
// is kept as a small scoped GlobalStyles (cannot be a styled JSX element).
const childrenContainerClassName = 'demo-page-children-container'

const childrenContainerStyles = {
  [`.${childrenContainerClassName}`]: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center'
  }
}

const PageContainer = styled(MainWrapper)({
  height: '100%'
})

const MediaContainer = styled('div', { shouldForwardProp: prop => prop !== 'tvMode' })<{ tvMode?: boolean }>(({ tvMode }) => ({
  ...(tvMode ? { width: '88vw' } : {})
}))

const TopbarWrapper = styled('div')({
  position: 'fixed',
  top: '.5rem',
  right: 16,
  zIndex: 10000000000,
  display: 'flex',
  touchAction: 'none'
})

const ControlButton = styled(GlassButton, {
  shouldForwardProp: prop => prop !== 'isActive'
})<{ isActive: boolean }>(({ isActive, theme }) => ({
  color: `${theme.palette.text.primary} !important`,
  minWidth: '3rem',
  width: '3rem',
  height: '3rem',
  margin: '5px !important',
  borderRadius: '10%',
  transition: 'background-color .4s ease',
  fontFamily: 'arial',
  background: isActive ? 'var(--btn-active-color) !important' : 'var(--btn-active-bg-color) !important'
}))

const ButtonText = styled(Typography)({
  color: 'var(--btn-text-color)'
})

type CurrentView = 'video' | 'img' | 'model' | null

export type VideoPageState = {
  demoMedia: {
    video: string
    img: string
    model: string
  }
}

export default function DemoPage () {
  const [view, setView] = useState<CurrentView>(null)
  const { demoMedia } = useDemoMedia()
  const { t } = useTranslation()
  const navigate = useYisNavigate()
  const tvMode = tvModeActive()
  const { main, propellers, engines, stabilizers, rudder, thruster, generators } = useSelector(
    (state: AppState) => state
  )

  const [vesselData] = useState({
    main,
    propellers,
    engines,
    stabilizers,
    rudder,
    thruster,
    generators
  })

  useEffect(() => {
    // determine which state to show
    if (demoMedia.video.path !== '') {
      setView('video')
    } else if (demoMedia.img) {
      setView('img')
    } else if (demoMedia.model) {
      setView('model')
    } else {
      navigate('/home')
    }
  }, [])

  const clearAll = () => {
    const video = document.querySelector('#theVideoContainer video')
    if (video) {
      video.remove()
    }
  }

  const showModel = () => {
    clearAll()
    setView('model')
  }

  const TopbarControls = () => <TopbarWrapper>
    {demoMedia.video.path !== '' && <Tooltip placement="top" title={t('Show video') || ''} classes={{ tooltip: 'menu_tooltip' }}>
      <Box>
        <ControlButton
          onClick={() => setView('video')}
          isActive={view === 'video'}
        >
          <img src={VideoIcon} alt="video" />
        </ControlButton>
      </Box>
    </Tooltip>}

    {demoMedia.img && <Tooltip placement="top" title={t('Show image') || ''} classes={{ tooltip: 'menu_tooltip' }}>
      <Box>
        <ControlButton
          isActive={view === 'img'}
          onClick={() => setView('img')}
        >
          <img src={ImageIcon} alt="Yacht Video" />
        </ControlButton>
      </Box>
    </Tooltip>}

    {demoMedia.model && <Tooltip placement="top" title={t('Show model') || ''} classes={{ tooltip: 'menu_tooltip' }}>
      <Box>
        <ControlButton
          isActive={view === 'model'}
          onClick={showModel}
        >
          <ButtonText variant="body1">
            3D
          </ButtonText>
        </ControlButton>
      </Box>
    </Tooltip>}
  </TopbarWrapper>

  return (
    <>
      <GlobalStyles styles={childrenContainerStyles} />

      <PageContainer
        className="three-d"
        containerClass={childrenContainerClassName}
      >
        <Grid
          container
          size={{ lg: 12 }}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center'
          }}>
          {!view && <Loader />}
          <TopbarControls />
          <MediaContainer className="media-container" tvMode={tvMode} id="theVideoContainer">
            {/* @ts-expect-error FIXME i'm deprecated */}
            {view === 'video' && <motion.div
              style={{ height: '100%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <video
                src={demoMedia.video.path}
                autoPlay
                loop
                className={demoMedia.video.isHorizontal ? 'media video-horizontal' : 'media video-vertical'}
                preload="metadata"
                muted
              />{' '}
            </motion.div>}

            {/* @ts-expect-error FIXME i'm deprecated */}
            {view === 'img' && <motion.div
              style={{ height: '100%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <img src={demoMedia.img} className="media" alt="yacht" />{' '}
            </motion.div>}

            {/* @ts-expect-error FIXME i'm deprecated */}
            {view === 'model' && <motion.div
              style={{ height: '100%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ThreeDView modelName={demoMedia.model} vesselData={vesselData} />
            </motion.div>}
          </MediaContainer>
        </Grid>
      </PageContainer>
    </>
  )
}
